import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Inbox, Package, School as SchoolIcon, Store,
  ShoppingBag, Search, RefreshCw, ChevronRight, X, CheckCircle2,
  TrendingUp, ArrowRight, ExternalLink, AlertTriangle, DollarSign,
  Truck, FileCheck2, Receipt, Wallet, Lock, Percent, ClipboardList,
  Settings as SettingsIcon,
} from "lucide-react";

const NAV = [
  { to: "/super/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/super/schools", label: "Schools", icon: SchoolIcon },
  { to: "/super/products", label: "Products", icon: Package },
  { to: "/super/vendors", label: "Vendors", icon: Store },
  { to: "/super/orders", label: "Orders & Ops", icon: ShoppingBag },
  { to: "/super/finance", label: "Finance", icon: Wallet },
  { to: "/super/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/super/settings", label: "Settings", icon: SettingsIcon },
];

const STAGES = [
  { key: "order.placed",           label: "New Order",             short: "New",         color: "slate",  icon: ShoppingBag },
  { key: "order.under_review",     label: "Under Review",          short: "Review",      color: "blue",   icon: ClipboardList },
  { key: "supplier.assigned",      label: "Supplier Assigned",     short: "Assigned",    color: "blue",   icon: Store },
  { key: "po.drafted",             label: "PO Draft",              short: "PO Draft",    color: "indigo", icon: FileCheck2 },
  { key: "po.issued",              label: "PO Issued",             short: "PO Issued",   color: "indigo", icon: Lock },
  { key: "vendor.acknowledged",    label: "Vendor Accepted",       short: "Accepted",    color: "amber",  icon: CheckCircle2 },
  { key: "dispatched",             label: "Dispatched",            short: "Dispatch",    color: "amber",  icon: Truck },
  { key: "delivered",              label: "Delivered",             short: "Delivered",   color: "amber",  icon: Truck },
  { key: "grn.completed",          label: "GRN Completed",         short: "GRN",         color: "emerald",icon: FileCheck2 },
  { key: "school_invoice.raised",  label: "School Invoice Raised", short: "Invoiced",    color: "emerald",icon: Receipt },
  { key: "payment.received",       label: "Payment Received",      short: "Paid",        color: "emerald",icon: Wallet },
  { key: "order.closed",           label: "Closed",                short: "Closed",      color: "slate",  icon: CheckCircle2 },
];

const STAGE_MAP = Object.fromEntries(STAGES.map((s) => [s.key, s]));

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function Orders() {
  const [tab, setTab] = useState("workflow");     // 'journal' | 'workflow'
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (stageFilter) params.stage = stageFilter;
      const [{ data }, { data: k }] = await Promise.all([
        api.get("/admin/orders", { params }),
        api.get("/admin/orders/kanban-counts"),
      ]);
      setRows(data);
      setCounts(k);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const stageMetrics = useMemo(() => {
    const total = rows.length;
    const closed = rows.filter((r) => r.chain_stage === "order.closed").length;
    const active = total - closed;
    const gmv = rows.reduce((s, r) => s + Number(r.total || 0), 0);
    return { total, active, closed, gmv };
  }, [rows]);

  return (
    <DashboardLayout nav={NAV} title="Orders & Procurement Operations">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-2xl font-heading font-semibold text-brand-navy" data-testid="orders-title">
            Orders &amp; Procurement Operations
          </div>
          <div className="text-sm text-slate-500">
            {stageMetrics.total} total · {stageMetrics.active} active · {stageMetrics.closed} closed · GMV {fmtINR(stageMetrics.gmv)}
          </div>
        </div>
        <div className="inline-flex rounded-md bg-slate-100 p-1 text-sm">
          <button
            data-testid="orders-tab-workflow"
            onClick={() => setTab("workflow")}
            className={`px-4 py-1.5 rounded ${tab === "workflow" ? "bg-white shadow font-semibold text-brand-navy" : "text-slate-600 hover:text-slate-900"}`}
          >Workflow</button>
          <button
            data-testid="orders-tab-journal"
            onClick={() => setTab("journal")}
            className={`px-4 py-1.5 rounded ${tab === "journal" ? "bg-white shadow font-semibold text-brand-navy" : "text-slate-600 hover:text-slate-900"}`}
          >Journal</button>
        </div>
      </div>

      {/* Search + Stage filter */}
      <div className="sl-card p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="orders-search"
            placeholder="Search order id, student, PO number…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9 pr-4 py-2 w-full rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue"
          />
        </div>
        {tab === "journal" && (
          <select data-testid="orders-stage-filter" value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white">
            <option value="">All stages</option>
            {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label} ({counts[s.key] || 0})</option>)}
          </select>
        )}
        <button onClick={load} data-testid="orders-refresh"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 text-sm hover:border-slate-400">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {tab === "workflow" ? (
        <KanbanView rows={rows} counts={counts} onOpen={(id) => setDetailId(id)} loading={loading} />
      ) : (
        <JournalView rows={rows} onOpen={(id) => setDetailId(id)} loading={loading} />
      )}

      {detailId && (
        <OrderDetail id={detailId} onClose={() => setDetailId(null)} onRefresh={load} />
      )}
    </DashboardLayout>
  );
}

/* ---------------- Kanban ---------------- */
function KanbanView({ rows, counts, onOpen, loading }) {
  const grouped = useMemo(() => {
    const g = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    for (const r of rows) {
      const s = r.chain_stage || "order.placed";
      if (g[s]) g[s].push(r);
    }
    return g;
  }, [rows]);

  return (
    <div className="overflow-x-auto pb-4" data-testid="orders-kanban">
      <div className="inline-flex gap-3 min-w-max">
        {STAGES.map((s) => (
          <div key={s.key} className="w-72 flex-shrink-0" data-testid={`kanban-col-${s.key}`}>
            <div className={`rounded-t-lg px-3 py-2 border-b-2 flex items-center gap-2 bg-${s.color}-50 border-${s.color}-400`}>
              <s.icon size={14} />
              <div className="text-xs font-semibold text-slate-800 uppercase tracking-wide">{s.short}</div>
              <div className="ml-auto text-xs font-semibold text-slate-500">
                {counts[s.key] || 0}
              </div>
            </div>
            <div className="rounded-b-lg bg-slate-50 border border-slate-200 border-t-0 p-2 min-h-[100px] space-y-2 max-h-[68vh] overflow-y-auto">
              {loading && (
                <div className="text-xs text-slate-500 text-center py-4">Loading…</div>
              )}
              {!loading && grouped[s.key].length === 0 && (
                <div className="text-xs text-slate-400 text-center py-6">—</div>
              )}
              {grouped[s.key].map((r) => (
                <button
                  key={r.id} onClick={() => onOpen(r.id)}
                  data-testid={`kanban-card-${r.id}`}
                  className="w-full text-left rounded-md bg-white border border-slate-200 hover:border-brand-blue hover:shadow-sm px-3 py-2 transition-all">
                  <div className="text-xs font-semibold text-slate-800 truncate">
                    {r.order_number || r.id.slice(0, 8)}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-500 truncate">{r.school_name || "—"}</div>
                  <div className="mt-1 flex items-center justify-between text-xs">
                    <span className="text-slate-500">{r.items?.length || 0} items</span>
                    <span className="font-semibold text-slate-800">{fmtINR(r.total)}</span>
                  </div>
                  {r.vendor_name && (
                    <div className="mt-1 text-[10px] text-brand-blueDark truncate">
                      → {r.vendor_name}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Journal ---------------- */
function JournalView({ rows, onOpen, loading }) {
  return (
    <div className="sl-card p-0 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-4 py-3">Order</th>
            <th className="text-left px-4 py-3">School</th>
            <th className="text-left px-4 py-3">Stage</th>
            <th className="text-left px-4 py-3">Vendor</th>
            <th className="text-right px-4 py-3">Items</th>
            <th className="text-right px-4 py-3">Amount</th>
            <th className="text-left px-4 py-3">Created</th>
            <th className="text-right px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={8} className="text-center py-10 text-slate-500">Loading…</td></tr>}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={8} className="text-center py-12 text-slate-500" data-testid="journal-empty">No orders match.</td></tr>
          )}
          {!loading && rows.map((r) => {
            const s = STAGE_MAP[r.chain_stage] || STAGE_MAP["order.placed"];
            return (
              <tr key={r.id}
                  onClick={() => onOpen(r.id)}
                  data-testid={`journal-row-${r.id}`}
                  className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer">
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-800">{r.order_number || r.id.slice(0, 8)}</div>
                  <div className="text-xs text-slate-500">{r.student_name || "—"}</div>
                </td>
                <td className="px-4 py-3 text-slate-600">{r.school_name || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full bg-${s.color}-50 text-${s.color}-800 text-xs font-semibold`}>
                    <s.icon size={11} /> {s.short}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.vendor_name || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-right text-slate-700">{r.items?.length || 0}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-800">{fmtINR(r.total)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {new Date(r.created_at).toLocaleDateString("en-IN")}
                </td>
                <td className="px-4 py-3 text-right"><ChevronRight size={14} className="inline text-slate-400" /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Detail drawer with workflow actions ---------------- */
function OrderDetail({ id, onClose, onRefresh }) {
  const [order, setOrder] = useState(null);
  const [margin, setMargin] = useState(null);
  const [events, setEvents] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAction, setShowAction] = useState(null);   // key of stage transition to run
  const [actionForm, setActionForm] = useState({});

  const load = async () => {
    try {
      const [{ data: chain }] = await Promise.all([
        api.get(`/admin/orders/${id}/chain`),
      ]);
      setOrder({ ...chain.order, chain_stage: chain.current_stage });
      setEvents(chain.events);
      // Load margin if PO is issued (harmless 404 otherwise)
      try {
        const { data: m } = await api.get(`/admin/orders/${id}/margin`);
        setMargin(m);
      } catch (_e) { setMargin(null); }
      // Load vendors list & recommendations for the FIRST item's category
      const firstCat = chain.order?.items?.[0]?.category || "";
      const [vRes, rRes] = await Promise.all([
        vendors.length ? Promise.resolve({ data: vendors })
                       : api.get("/admin/vendors?active=true"),
        firstCat
          ? api.get(`/admin/vendors/recommendations/for-category?category=${encodeURIComponent(firstCat)}`)
          : Promise.resolve({ data: [] }),
      ]);
      if (!vendors.length) setVendors(vRes.data);
      setRecommended(rRes.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!order) return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="order-detail">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-3xl bg-white border-l border-slate-200 p-8 text-center text-slate-500">Loading…</div>
    </div>
  );

  const stage = STAGE_MAP[order.chain_stage] || STAGE_MAP["order.placed"];

  const runTransition = async (path, body = {}) => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${id}/${path}`, body);
      toast.success("Stage advanced");
      setShowAction(null); setActionForm({});
      await load(); onRefresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const nextAction = getNextAction(order.chain_stage);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="order-detail">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-3xl bg-white border-l border-slate-200 shadow-xl overflow-y-auto">
        <div className="p-6 border-b border-slate-100 sticky top-0 bg-white z-10 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-brand-greenDark font-semibold">Order</div>
            <h3 className="mt-1 text-xl font-heading font-semibold text-brand-navy truncate">
              {order.order_number || order.id.slice(0, 8)}
            </h3>
            <div className="text-xs text-slate-500 mt-0.5 truncate">
              {order.student_name} · {order.school_id ? "School order" : "Parent order"} · {new Date(order.created_at).toLocaleString("en-IN")}
            </div>
          </div>
          <button onClick={onClose} data-testid="order-detail-close"
            className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-6 text-sm">
          {/* Stage strip */}
          <StageStrip current={order.chain_stage} />

          {/* Amounts */}
          <div className="grid grid-cols-4 gap-3">
            <StatCard icon={ShoppingBag} label="Selling total" value={fmtINR(order.total)} />
            <StatCard icon={DollarSign} label="Expected cost"
              value={margin ? fmtINR(margin.expected_cost_total) : "—"} />
            <StatCard icon={Percent} label="Expected margin"
              value={margin ? `${margin.expected_margin_pct}%` : "—"}
              hint={margin ? fmtINR(margin.expected_margin) : ""} />
            <StatCard icon={TrendingUp} label="Realized margin"
              value={margin?.realized_margin_pct != null ? `${margin.realized_margin_pct}%` : "—"}
              hint={margin?.realized_margin != null ? fmtINR(margin.realized_margin) : ""}
              variance={margin?.variance_pct} />
          </div>

          {/* Next action */}
          {nextAction && (
            <section className="rounded-xl border border-brand-blue/30 bg-brand-blueSoft/40 p-4">
              <div className="flex items-center gap-2 text-brand-blueDark font-semibold text-xs uppercase tracking-widest mb-2">
                <ArrowRight size={14} /> Next action
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-base font-heading font-semibold text-brand-navy">
                    {nextAction.label}
                  </div>
                  {nextAction.help && <div className="text-xs text-slate-600 mt-0.5">{nextAction.help}</div>}
                </div>
                <button
                  data-testid={`order-action-${nextAction.path}`}
                  onClick={() => nextAction.form ? setShowAction(nextAction.path) : runTransition(nextAction.path)}
                  disabled={busy}
                  className="px-4 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark disabled:opacity-60">
                  {busy ? "Working…" : nextAction.cta || "Advance"}
                </button>
              </div>

              {/* Inline forms per transition */}
              {showAction === "assign-supplier" && (
                <div className="mt-4 space-y-2" data-testid="assign-supplier-form">
                  {recommended.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-brand-greenDark font-semibold mb-1">
                        Recommended for "{order.items?.[0]?.category || "this category"}"
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {recommended.slice(0, 5).map((v) => (
                          <button key={v.id}
                            data-testid={`recommended-${v.id}`}
                            onClick={() => setActionForm({ vendor_id: v.id })}
                            className={`px-3 py-1.5 rounded-full border text-xs ${
                              actionForm.vendor_id === v.id
                                ? "border-brand-blue bg-brand-blue text-white font-semibold"
                                : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
                            }`}>
                            {v.company_name} · {v.avg_margin_pct}% margin · {v.past_pos} POs
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Or pick any active vendor</label>
                    <select value={actionForm.vendor_id || ""}
                      data-testid="assign-vendor-select"
                      onChange={(e) => setActionForm((s) => ({ ...s, vendor_id: e.target.value }))}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white">
                      <option value="">Choose vendor…</option>
                      {vendors.map((v) => <option key={v.id} value={v.id}>{v.company_name}</option>)}
                    </select>
                  </div>
                  <textarea rows={2} placeholder="Allocation notes (optional)"
                    value={actionForm.notes || ""}
                    onChange={(e) => setActionForm((s) => ({ ...s, notes: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-xs" />
                  <div className="flex gap-2">
                    <button onClick={() => runTransition("assign-supplier", actionForm)}
                      disabled={!actionForm.vendor_id || busy}
                      data-testid="assign-supplier-confirm"
                      className="px-4 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark disabled:opacity-50">
                      Assign vendor
                    </button>
                    <button onClick={() => { setShowAction(null); setActionForm({}); }}
                      className="px-4 py-2 rounded-md border border-slate-300 text-sm">Cancel</button>
                  </div>
                </div>
              )}

              {showAction === "dispatch" && (
                <div className="mt-4 grid sm:grid-cols-2 gap-2" data-testid="dispatch-form">
                  <input placeholder="AWB / tracking number"
                    data-testid="dispatch-awb"
                    value={actionForm.awb_number || ""}
                    onChange={(e) => setActionForm((s) => ({ ...s, awb_number: e.target.value }))}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  <input placeholder="Courier"
                    value={actionForm.courier || ""}
                    onChange={(e) => setActionForm((s) => ({ ...s, courier: e.target.value }))}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  <div className="sm:col-span-2 flex gap-2">
                    <button onClick={() => runTransition("dispatch", actionForm)} disabled={busy}
                      data-testid="dispatch-confirm"
                      className="px-4 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark">
                      Mark dispatched
                    </button>
                    <button onClick={() => { setShowAction(null); setActionForm({}); }}
                      className="px-4 py-2 rounded-md border border-slate-300 text-sm">Cancel</button>
                  </div>
                </div>
              )}

              {showAction === "complete-grn" && (
                <div className="mt-4 grid sm:grid-cols-2 gap-2" data-testid="grn-form">
                  <input placeholder="Received by (name)"
                    value={actionForm.received_by || ""}
                    onChange={(e) => setActionForm((s) => ({ ...s, received_by: e.target.value }))}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  <select value={actionForm.condition || "ok"}
                    onChange={(e) => setActionForm((s) => ({ ...s, condition: e.target.value }))}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white">
                    <option value="ok">All items received in good condition</option>
                    <option value="partial">Partial delivery</option>
                    <option value="damaged">Damage / shortage</option>
                  </select>
                  <div className="sm:col-span-2 flex gap-2">
                    <button onClick={() => runTransition("complete-grn", actionForm)} disabled={busy}
                      data-testid="grn-confirm"
                      className="px-4 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark">
                      Complete GRN
                    </button>
                    <button onClick={() => { setShowAction(null); setActionForm({}); }}
                      className="px-4 py-2 rounded-md border border-slate-300 text-sm">Cancel</button>
                  </div>
                </div>
              )}

              {showAction === "raise-school-invoice" && (
                <div className="mt-4 grid sm:grid-cols-2 gap-2" data-testid="invoice-form">
                  <input type="number" step="0.5" placeholder="GST %"
                    value={actionForm.gst_pct ?? 18}
                    onChange={(e) => setActionForm((s) => ({ ...s, gst_pct: Number(e.target.value) }))}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  <input type="number" placeholder="Due days" value={actionForm.due_days ?? 30}
                    onChange={(e) => setActionForm((s) => ({ ...s, due_days: Number(e.target.value) }))}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  <div className="sm:col-span-2 flex gap-2">
                    <button onClick={() => runTransition("raise-school-invoice", actionForm)} disabled={busy}
                      data-testid="invoice-confirm"
                      className="px-4 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark">
                      Raise invoice
                    </button>
                    <button onClick={() => { setShowAction(null); setActionForm({}); }}
                      className="px-4 py-2 rounded-md border border-slate-300 text-sm">Cancel</button>
                  </div>
                </div>
              )}

              {showAction === "record-payment" && (
                <div className="mt-4 grid sm:grid-cols-2 gap-2" data-testid="payment-form">
                  <input type="number" step="0.01" placeholder={`Amount (school invoice = ${fmtINR(order.school_invoice_total || 0)})`}
                    value={actionForm.amount ?? ""} required
                    data-testid="payment-amount"
                    onChange={(e) => setActionForm((s) => ({ ...s, amount: Number(e.target.value) }))}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  <select value={actionForm.method || "bank"}
                    onChange={(e) => setActionForm((s) => ({ ...s, method: e.target.value }))}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white">
                    <option value="bank">Bank transfer</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                    <option value="razorpay">Razorpay</option>
                  </select>
                  <input placeholder="Reference / UTR" className="sm:col-span-2 rounded-md border border-slate-300 px-3 py-2 text-sm"
                    value={actionForm.reference || ""}
                    onChange={(e) => setActionForm((s) => ({ ...s, reference: e.target.value }))} />
                  <div className="sm:col-span-2 flex gap-2">
                    <button onClick={() => runTransition("record-payment", actionForm)} disabled={busy || !actionForm.amount}
                      data-testid="payment-confirm"
                      className="px-4 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark disabled:opacity-50">
                      Record payment
                    </button>
                    <button onClick={() => { setShowAction(null); setActionForm({}); }}
                      className="px-4 py-2 rounded-md border border-slate-300 text-sm">Cancel</button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Items */}
          <section>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Line items</div>
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white">
              {(order.items || []).map((it, i) => (
                <div key={i} className="px-3 py-2 flex items-center justify-between text-xs" data-testid={`order-item-${i}`}>
                  <div>
                    <div className="font-semibold text-slate-800">{it.product_name || it.name || it.product_id}</div>
                    <div className="text-slate-500">{it.category} · qty {it.qty}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-slate-700">{fmtINR((it.unit_price || it.price || 0) * (it.qty || 1))}</div>
                    <div className="text-slate-400 text-[10px]">MRP {fmtINR(it.unit_price || it.price)}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Chain events */}
          <section>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Traceability</div>
            <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100" data-testid="order-chain">
              {events.map((e, i) => {
                const s = STAGE_MAP[e.event_type];
                return (
                  <div key={i} className="px-3 py-2 flex items-center gap-3 text-xs">
                    <div className={`w-6 h-6 rounded-full grid place-items-center bg-${s?.color || "slate"}-100 text-${s?.color || "slate"}-700`}>
                      {s ? <s.icon size={12} /> : <ArrowRight size={12} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800">{s?.label || e.event_type}</div>
                      {e.notes && <div className="text-slate-500 truncate">{e.notes}</div>}
                    </div>
                    <div className="text-right text-slate-500 whitespace-nowrap">
                      {e.amount != null && <div className="font-semibold text-slate-700">{fmtINR(e.amount)}</div>}
                      <div className="text-[10px]">{(e.actor || {}).name || "—"}</div>
                      <div className="text-[10px] text-slate-400">
                        {new Date(e.occurred_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function getNextAction(stage) {
  switch (stage) {
    case "order.placed":           return { path: "start-review",         label: "Start review",           cta: "Start review" };
    case "order.under_review":     return { path: "assign-supplier",      label: "Assign supplier",         form: true, help: "Review the catalog, then allocate this order to a vendor." };
    case "supplier.assigned":      return { path: "draft-po",             label: "Draft purchase order",    cta: "Draft PO", help: "Locks the expected cost + margin snapshot." };
    case "po.drafted":             return { path: "issue-po",             label: "Issue PO to vendor",      cta: "Issue PO", help: "Sends the PO. Margin is locked at this moment." };
    case "po.issued":              return { path: "vendor-acknowledge",   label: "Vendor has acknowledged",  cta: "Mark acknowledged" };
    case "vendor.acknowledged":    return { path: "dispatch",             label: "Vendor has dispatched",    form: true, help: "Capture AWB + courier before advancing." };
    case "dispatched":             return { path: "mark-delivered",       label: "Confirm delivered",        cta: "Mark delivered" };
    case "delivered":              return { path: "complete-grn",         label: "Complete GRN",             form: true, help: "School acknowledges receipt condition." };
    case "grn.completed":          return { path: "raise-school-invoice", label: "Raise school invoice",     form: true, help: "Bills the school. Creates a receivable." };
    case "school_invoice.raised":  return { path: "record-payment",       label: "Record school payment",    form: true, help: "Marks the receivable paid + reveals realized margin." };
    case "payment.received":       return { path: "close",                label: "Close order",              cta: "Close order", help: "Locks margin variance." };
    default: return null;
  }
}

function StageStrip({ current }) {
  const idx = STAGES.findIndex((s) => s.key === current);
  return (
    <div className="flex items-center gap-1 overflow-x-auto" data-testid="stage-strip">
      {STAGES.map((s, i) => {
        const active = i === idx;
        const done = i < idx;
        return (
          <div key={s.key} className="flex items-center gap-1">
            <div
              data-testid={`strip-${s.key}`}
              className={`px-2 py-1 rounded text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap ${
                active ? "bg-brand-blue text-white" :
                done ? "bg-brand-greenSoft text-brand-greenDark" :
                "bg-slate-100 text-slate-500"
              }`}>
              {s.short}
            </div>
            {i < STAGES.length - 1 && <ChevronRight size={10} className="text-slate-300" />}
          </div>
        );
      })}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, variance }) {
  const varianceCol = variance == null ? "" :
    variance > 0 ? "text-brand-greenDark" :
    variance < 0 ? "text-red-600" : "text-slate-500";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500 uppercase tracking-wide">
        <Icon size={12} /> {label}
      </div>
      <div className="mt-1 text-lg font-heading font-bold text-brand-navy">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      {variance != null && (
        <div className={`text-[11px] mt-0.5 font-semibold ${varianceCol}`}>
          Δ {variance > 0 ? "+" : ""}{variance}% vs expected
        </div>
      )}
    </div>
  );
}
